import { useState } from "react"
import { useStores } from "hooks/useStores"
import styled from "styled-components"

import type { LocationType } from "mobx/Localization/types";
import { defaultLocationsDetails } from "mobx/Localization/types"

import { StyledBackIcon, StyledHeaderTypography , StyledLocationOnMap, StyledPlaceTypography } from "../AddressSelection/AddressEntry.styles"
import { respondAbove } from "styles/mixins"
import { getTranslatedTextByKey } from "utils/language"

import { StyledCloseIcon, StepHeader } from 'components/localization/style'
import LocationTypes from "./LocationTypes"
import LocationInformations from "./LocationInformations"
import ButtonBase from "components/common/ButtonBase"


export const Container = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	padding: 20px 20px 0px 20px;

	${respondAbove.sm`
		padding: 28px 28px 0px 28px;
	`}
`

export const Content = styled.div`
    display: flex;
	flex-direction: column;
    gap: 40px;
    justify-content: space-between;
    height: 100%;
`

const LocationTypeButtonsContainer = styled.div`
    display: flex;
    gap: 20px;
    flex-direction: column;
`

const LocationDetailsSelection = () => {

    const { localizationStore } = useStores()
    const [selectedLocationType, setSelectedLocationType] = useState<LocationType | undefined>(localizationStore.processingFlow?.locationDetails?.type || undefined)
    const [locationTypeClicked, setLocationTypeClicked] = useState(false)

    const getDefaultLocationsDetails = (type: LocationType) => defaultLocationsDetails[type]!.map((field) => ({...field}))

    const locationInformations = 
        (localizationStore.processingFlow?.locationDetails?.type === selectedLocationType && localizationStore.processingFlow?.locationDetails?.informations) 
        || (selectedLocationType && getDefaultLocationsDetails(selectedLocationType))

    const onBack = () => {
       setLocationTypeClicked((value) => !value)
       if(!locationTypeClicked) {
            localizationStore.previousStep()
        }
    }
    
    const onContinue = async () => {
        if(selectedLocationType) {
            localizationStore.processingFlow?.setLocationDetails({
                type: selectedLocationType,
                informations: locationInformations
            })
        }

        localizationStore.nextStep()
    }

	return (
        <Container>
            <StepHeader>
                <StyledBackIcon data-testid="back-button" onClick={onBack} />
                <StyledHeaderTypography variant="SubheadHighlighted">
                    {!locationTypeClicked && getTranslatedTextByKey('webviewFlow.localization.locationType', 'Select location type')}
                    {locationTypeClicked && getTranslatedTextByKey('webviewFlow.localization.additionalInfo', 'Additional Info')}
                </StyledHeaderTypography>
                <StyledCloseIcon data-testid="close-button" onClick={localizationStore.abort} />
            </StepHeader>

            <Content>
                {!locationTypeClicked &&
                    <LocationTypes 
                        type={selectedLocationType} 
                        onClickingType={(type) => {
                            setSelectedLocationType(type)
                            setLocationTypeClicked(true)
                        }}
                    />
                }
                {locationTypeClicked &&
                    <LocationInformations 
                        type={selectedLocationType} 
                        fields={locationInformations}
                    />
                }

                <LocationTypeButtonsContainer>
                    {locationTypeClicked &&
                        <ButtonBase data-testid="continue-button" onClick={onContinue}>
                            {getTranslatedTextByKey('webviewFlow.localization.continue', 'Continue')}
                        </ButtonBase>
                    }

                    <StyledLocationOnMap data-testid="skip-on-location" onClick={localizationStore.nextStep}>
                        <StyledPlaceTypography variant="CTATextSmall">
                            {getTranslatedTextByKey('webviewFlow.localization.skip', 'Skip')}
                        </StyledPlaceTypography>
                    </StyledLocationOnMap>
                </LocationTypeButtonsContainer>
            </Content>
        </Container>
	)
}

export default LocationDetailsSelection
